import React from 'react';
import {
  Box, ButtonBase, Checkbox, CircularProgress,
} from '@mui/material';
import type { Theme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { plr, SHORT_DESKTOP } from './tokens';
import {
  alertCircleIcon, checkCircleIcon, chevronLeftIcon, clockIcon, shieldCheckIcon,
} from './icons';
import { usePlrLayout } from './layoutContext';
import type { PlrThumbShape } from './layoutContext';

type Sx = SystemStyleObject<Theme>;

// Variante imagen=mini: fila del proyecto en el header del shell (entre el divider de los
// logos y la barra de pasos), con la foto como miniatura. Con glass, la fila va en una card
// cuyo fondo es la misma foto muy desenfocada bajo un velo claro.
export function ProjectRow({
  name, company, thumb, shape, glass,
}: {
  name: string,
  company?: string,
  thumb: string,
  shape: PlrThumbShape,
  glass: boolean,
}) {
  const rect = shape === 'rect';
  const layerSx = {
    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none',
  } as const;
  return (
    <Box
      data-pw="preloan-project-row"
      data-glass={glass ? 'true' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        ...(glass && {
          position: 'relative',
          isolation: 'isolate',
          overflow: 'hidden',
          alignSelf: 'flex-start',
          width: 'fit-content',
          maxWidth: '100%',
          boxSizing: 'border-box',
          p: '7px 18px 7px 7px',
          borderRadius: rect ? '16px' : '18px',
          border: '1px solid rgba(255,255,255,0.85)',
          boxShadow: `inset 0 1px 0 rgba(255,255,255,0.9), 0 0 0 1px ${plr.divider}, 0 8px 20px rgba(41,29,94,0.08)`,
        }),
      }}
    >
      {glass && (
        <>
          <Box
            component="img"
            src={thumb}
            alt=""
            aria-hidden="true"
            sx={{
              ...layerSx,
              top: '-30%',
              left: '-10%',
              width: '120%',
              height: '160%',
              objectFit: 'cover',
              filter: 'blur(20px) saturate(1.6) brightness(1.08)',
              zIndex: -2,
            }}
          />
          <Box
            aria-hidden="true"
            sx={{
              ...layerSx,
              zIndex: -1,
              background: `linear-gradient(110deg, rgba(255,255,255,0.42) 0%, ${plr.page}D9 70%)`,
            }}
          />
        </>
      )}
      <Box
        component="img"
        src={thumb}
        alt=""
        data-pw="preloan-project-thumb"
        sx={{
          width: rect ? 76 : 48,
          height: 48,
          [SHORT_DESKTOP]: { width: rect ? 60 : 38, height: 38 },
          flexShrink: 0,
          borderRadius: rect ? '10px' : '12px',
          objectFit: 'cover',
          display: 'block',
          boxShadow: glass ? '0 0 0 1.5px rgba(255,255,255,0.9), 0 2px 6px rgba(26,20,61,0.12)' : undefined,
        }}
      />
      <Box sx={{
        display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0,
      }}
      >
        {company && (
          <Box
            component="span"
            sx={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: plr.muted,
            }}
          >
            {company}
          </Box>
        )}
        <Box
          component="span"
          data-pw="preloan-project-name"
          sx={{
            fontSize: 15.5, fontWeight: 800, color: plr.ink, lineHeight: 1.25,
          }}
        >
          {name}
        </Box>
      </Box>
    </Box>
  );
}

ProjectRow.defaultProps = { company: undefined };

export function ProjectLabel({ children, hideOnPanel }: {
  children: React.ReactNode,
  hideOnPanel: boolean,
}) {
  // Se oculta donde el nombre ya se ve: en el panel lateral (desktop), sobre la foto o en
  // la fila del proyecto del header (imagen=mini).
  const {
    panel, nameOnPhotoMobile, nameOnPhotoDesktop, projectInHeader,
  } = usePlrLayout();
  if (projectInHeader && hideOnPanel) return null;
  const shown = (hidden: boolean, visible: string) => (hideOnPanel && hidden ? 'none' : visible);
  const display = (visible: string) => ({
    xs: shown(nameOnPhotoMobile, visible),
    sm: shown(nameOnPhotoDesktop, visible),
    lg: shown(panel || nameOnPhotoDesktop, visible),
  });
  return (
    <Box
      component="span"
      data-pw="preloan-project-name"
      sx={{
        display: display('inline'),
        fontSize: { xs: 12, sm: 12.5 },
        fontWeight: 700,
        letterSpacing: '0.8px',
        textTransform: 'uppercase',
        color: plr.accent,
      }}
    >
      {children}
    </Box>
  );
}

// "Paso 1 de 3" junto a "Tu RUT" en mobile, solo cuando la barra de pasos no se ve ahí.
export function MobileStepHint({ children }: { children: React.ReactNode }) {
  const { stepBarOnMobile } = usePlrLayout();
  if (stepBarOnMobile) return null;
  return (
    <Box
      component="span"
      data-pw="preloan-step-label"
      sx={{ display: { xs: 'inline', sm: 'none' }, fontSize: 12.5, color: plr.muted }}
    >
      {children}
    </Box>
  );
}

// Chip informativo sobre el título de 1f: "Cerca de 3 minutos · Sin costo".
export function TimeChip({ children }: { children: React.ReactNode }) {
  return (
    <Box
      component="span"
      data-pw="preloan-time-chip"
      sx={{
        alignSelf: 'flex-start',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        background: plr.chip,
        borderRadius: 999,
        p: { xs: '5px 11px', sm: '6px 12px' },
        fontSize: { xs: 12.5, sm: 13 },
        fontWeight: 600,
        color: plr.accent,
      }}
    >
      {clockIcon({ size: 14 })}
      {children}
    </Box>
  );
}

// Link "Volver" con flecha a la izquierda: el de bajo la barra de pasos y el "Volver a
// {partner}" de los resultados (4f/4fb/4fc) usan el mismo estilo.
export function PlrBackLink({
  children, onClick, type, testId, hidden, sx,
}: {
  children: React.ReactNode,
  onClick?: () => void,
  type?: 'button' | 'submit',
  testId?: string,
  // Ocupa su lugar sin verse (para reservar el alto y que el contenido no salte).
  hidden?: boolean,
  sx?: Sx,
}) {
  return (
    <ButtonBase
      type={type}
      data-pw={hidden ? undefined : testId}
      aria-hidden={hidden ? true : undefined}
      tabIndex={hidden ? -1 : 0}
      onClick={onClick}
      sx={{
        height: 32,
        ml: '-6px',
        px: '6px',
        gap: '4px',
        alignSelf: 'flex-start',
        borderRadius: '10px',
        fontFamily: plr.font,
        fontSize: 14,
        fontWeight: 600,
        color: plr.ink,
        textAlign: 'left',
        visibility: hidden ? 'hidden' : 'visible',
        '&:hover': { color: plr.inkHover },
        ...sx,
      }}
    >
      {chevronLeftIcon({ size: 18 })}
      {children}
    </ButtonBase>
  );
}

PlrBackLink.defaultProps = {
  onClick: undefined, type: 'button', testId: undefined, hidden: false, sx: undefined,
};

type TitleSize = 'hero' | 'step' | 'result';

const titleSizes: Record<TitleSize, Sx> = {
  hero: {
    fontSize: { xs: 26, sm: 36, lg: 42 },
    lineHeight: { xs: 1.2, sm: 1.12 },
    letterSpacing: { xs: '-0.8px', sm: '-1.3px' },
    [SHORT_DESKTOP]: { fontSize: 32 },
  },
  step: {
    fontSize: { xs: 26, sm: 34 },
    lineHeight: { xs: 1.2, sm: 1.14 },
    letterSpacing: { xs: '-0.8px', sm: '-1.1px' },
    [SHORT_DESKTOP]: { fontSize: 30 },
  },
  result: {
    fontSize: { xs: 26, sm: 34, lg: 38 },
    lineHeight: { xs: 1.2, sm: 1.14 },
    letterSpacing: { xs: '-0.8px', sm: '-1.1px' },
    [SHORT_DESKTOP]: { fontSize: 30 },
  },
};

export function PlrTitle({
  children, size, testId, sx,
}: {
  children: React.ReactNode,
  size: TitleSize,
  testId?: string,
  sx?: Sx,
}) {
  return (
    <Box
      component="h1"
      data-pw={testId}
      sx={{
        m: 0,
        fontWeight: 800,
        color: plr.ink,
        fontFamily: plr.font,
        ...titleSizes[size],
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

PlrTitle.defaultProps = { testId: undefined, sx: undefined };

export function PlrLead({
  children, testId, large, sx,
}: {
  children: React.ReactNode,
  testId?: string,
  large?: boolean,
  sx?: Sx,
}) {
  return (
    <Box
      component="p"
      data-pw={testId}
      sx={{
        m: '-4px 0 0',
        fontSize: { xs: 15, sm: large ? 18 : 17 },
        lineHeight: 1.5,
        color: plr.muted,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

PlrLead.defaultProps = { testId: undefined, large: false, sx: undefined };

const buttonBase = {
  height: { xs: 54, sm: 56 },
  [SHORT_DESKTOP]: { height: 50 },
  flexShrink: 0,
  boxSizing: 'border-box',
  borderRadius: '28px',
  px: '28px',
  fontFamily: plr.font,
  fontSize: 17,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  whiteSpace: 'nowrap',
  transition: 'background-color .15s, border-color .15s, color .15s',
  '&.Mui-focusVisible': { outline: `3px solid ${plr.stepCurrent}`, outlineOffset: '2px' },
} as const;

export const primaryButtonSx = {
  ...buttonBase,
  background: plr.ink,
  color: '#FFFFFF',
  '&:hover': { background: plr.inkHover },
  '&.Mui-disabled': { background: '#B9B3D1', color: '#FFFFFF' },
} as const;

export const outlinedButtonSx = {
  ...buttonBase,
  border: `2px solid ${plr.ink}`,
  background: '#FFFFFF',
  color: plr.ink,
  '&:hover': { borderColor: plr.inkHover, color: plr.inkHover },
  '&.Mui-disabled': { borderColor: '#B9B3D1', color: '#B9B3D1' },
} as const;

type PlrButtonProps = {
  children: React.ReactNode,
  variant?: 'contained' | 'outlined',
  type?: 'button' | 'submit',
  onClick?: () => void,
  disabled?: boolean,
  loading?: boolean,
  testId?: string,
  sx?: Sx,
};

export function PlrButton(props: PlrButtonProps) {
  const {
    children, variant = 'contained', type = 'button', onClick, disabled = false, loading = false, testId, sx,
  } = props;
  return (
    <ButtonBase
      type={type}
      data-pw={testId}
      onClick={onClick}
      disabled={disabled || loading}
      sx={{
        ...(variant === 'contained' ? primaryButtonSx : outlinedButtonSx),
        ...sx,
      } as Sx}
    >
      {loading ? <CircularProgress size={22} sx={{ color: 'inherit' }} /> : children}
    </ButtonBase>
  );
}

PlrButton.defaultProps = {
  variant: 'contained',
  type: 'button',
  onClick: undefined,
  disabled: false,
  loading: false,
  testId: undefined,
  sx: undefined,
};

export function PlrFieldLabel({ htmlFor, children, aside }: {
  htmlFor: string,
  children: React.ReactNode,
  aside?: React.ReactNode,
}) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <Box component="label" htmlFor={htmlFor} sx={{ fontSize: { xs: 14, sm: 15 }, fontWeight: 700, color: plr.ink }}>
        {children}
      </Box>
      {aside}
    </Box>
  );
}

PlrFieldLabel.defaultProps = { aside: undefined };

// Estilos del TextField (MUI) para que calce con los inputs del canvas.
export const plrInputSx = {
  '& .MuiOutlinedInput-root': {
    height: { xs: 54, sm: 56 },
    [SHORT_DESKTOP]: { height: 50 },
    borderRadius: '16px',
    background: '#FFFFFF',
    fontFamily: plr.font,
    fontSize: { xs: 16, sm: 17 },
    color: plr.ink,
    pl: '4px',
  },
  '& .MuiOutlinedInput-input::placeholder': { color: plr.faint, opacity: 1 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: plr.border, borderWidth: '1px' },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: plr.faint },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: plr.ink, borderWidth: '2px' },
  '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: '#C0392B' },
  '& .MuiFormHelperText-root': { mx: '4px', fontFamily: plr.font },
} as const;

type NoticeTone = 'error' | 'info' | 'success' | 'secure';
const noticeIcons: Record<NoticeTone, () => React.ReactNode> = {
  error: () => alertCircleIcon({ size: 18, strokeWidth: 2.2 }),
  info: () => clockIcon({ size: 18 }),
  success: () => checkCircleIcon({ size: 18, strokeWidth: 2.2 }),
  secure: () => shieldCheckIcon({ size: 18 }),
};
const noticeColors: Record<NoticeTone, string> = {
  error: plr.danger, info: plr.accent, success: '#1E7B4F', secure: plr.accent,
};

// Caja gris con ícono para errores y avisos dentro de los pasos (mismo estilo que el error de 3f).
export function PlrNotice({
  tone = 'info', children, testId, action, sx,
}: {
  tone?: NoticeTone,
  children: React.ReactNode,
  testId?: string,
  // Link o botón al final del texto ("Reintentar", "Contáctanos").
  action?: React.ReactNode,
  sx?: Sx,
}) {
  return (
    <Box
      role={tone === 'error' ? 'alert' : undefined}
      data-pw={testId}
      sx={{
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
        background: plr.surface,
        borderRadius: '16px',
        p: '14px 16px',
        fontSize: { xs: 13.5, sm: 14.5 },
        lineHeight: 1.45,
        color: plr.body,
        ...sx,
      }}
    >
      <Box component="span" sx={{ color: noticeColors[tone], mt: '1px', display: 'flex' }}>
        {noticeIcons[tone]()}
      </Box>
      <Box sx={{
        display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0,
      }}
      >
        <span>{children}</span>
        {action}
      </Box>
    </Box>
  );
}

PlrNotice.defaultProps = {
  tone: 'info', testId: undefined, action: undefined, sx: undefined,
};

// Link de texto subrayado (abre hojas de ayuda, "Cambiar", "Reintentar").
export function PlrTextLink({
  children, onClick, testId, sx,
}: {
  children: React.ReactNode,
  onClick: () => void,
  testId?: string,
  sx?: Sx,
}) {
  return (
    <ButtonBase
      type="button"
      data-pw={testId}
      onClick={onClick}
      sx={{
        alignSelf: 'flex-start',
        fontFamily: plr.font,
        fontSize: 'inherit',
        fontWeight: 600,
        color: plr.accent,
        textDecoration: 'underline',
        textUnderlineOffset: '2px',
        borderRadius: '4px',
        '&:hover': { color: plr.ink },
        '&.Mui-focusVisible': { outline: `2px solid ${plr.stepCurrent}`, outlineOffset: '2px' },
        ...sx,
      }}
    >
      {children}
    </ButtonBase>
  );
}

PlrTextLink.defaultProps = { testId: undefined, sx: undefined };

// Checkbox de consentimiento del rediseño (mismo estilo que los de ClaveÚnica): todo el texto
// es clickeable, salvo los links que vengan dentro.
export function PlrConsent({
  testId, checked, onToggle, disabled, children,
}: {
  testId: string,
  checked: boolean,
  onToggle: () => void,
  disabled?: boolean,
  children: React.ReactNode,
}) {
  return (
    <Box
      component="label"
      sx={{
        display: 'flex',
        gap: '10px',
        alignItems: 'flex-start',
        fontSize: { xs: 13.5, sm: 14 },
        lineHeight: 1.45,
        color: plr.ink,
        cursor: 'pointer',
      }}
    >
      <Checkbox
        data-pw={testId}
        checked={checked}
        onClick={onToggle}
        disabled={disabled}
        disableRipple
        sx={{
          p: 0,
          mt: '-1px',
          color: plr.faint,
          '&.Mui-checked': { color: plr.ink },
          '& .MuiSvgIcon-root': { fontSize: 24 },
        }}
      />
      <span>{children}</span>
    </Box>
  );
}

PlrConsent.defaultProps = { disabled: false };
